import React from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from 'urql';
import CONTENTO_TYPES from '../../domain/ContentoTypes';
import { isBrowser } from '../../utils/environment';
import { createImageProxyUrl } from '../../utils/svtimage';
import VideoPlayerHandlerContainer from '../VideoPlayer/new/VideoPlayerHandlerContainer';
import VIDEO_MODAL_QUERY from './VideoModal.graphql';
import VideoModalError from './VideoModalError';
import { Container, SlashAnimationWrapper, SlashAnimation } from './VideoModal.styles';
import FocusTrap from 'focus-trap-react';

const VideoModalPlay = ({ data, id }) => {
    if (!data || !data.detailsPage) {
        return null;
    }

    const {
        detailsPage: {
            item: {
                barn,
                restrictions: { blockedForChildren },
                parent,
                name: episodeTitle,
                __typename,
                tags,
                highlights,
                urls: { svtplay: url },
                videos,
                endCreditStart, // A/B-test Skip Credits sep 2021
                duration, // A/B-test Skip Credits sep 2021
            },
            svtId,
            id: visionId,
            images,
        },
        associatedContent,
    } = data;

    // A/B-test Skip Credits sep 2021
    const nextVideoUrl = data?.playQueue?.nextToPlay?.item?.urls.svtplay;
    const nextVideoId = data?.playQueue?.nextToPlay?.item?.svtId;

    const typeName =
        __typename === CONTENTO_TYPES.VARIANT || __typename === CONTENTO_TYPES.CLIP
            ? parent
                ? parent.__typename
                : __typename
            : __typename;

    const posterImage = createImageProxyUrl(images.wide.id, images.wide.changed, 'large', 1024);

    const isKidsGenre = tags.some((tag) => tag.slug === 'barn' || tag.slug === 'barn-oppet-arkiv');

    return (
        <VideoPlayerHandlerContainer
            barn={barn}
            visionId={visionId}
            escenicId={id}
            titleArticleId={parent ? parent.id : null}
            title={parent ? parent.name : episodeTitle}
            episodeTitle={episodeTitle}
            posterImage={posterImage}
            autoPlay={true}
            blockedForChildren={blockedForChildren}
            svtId={svtId}
            __typename={typeName}
            __videoType={__typename}
            isKidsGenre={isKidsGenre}
            highlights={highlights}
            url={url}
            videos={videos}
            parentUrl={parent.urls.svtplay}
            associatedContent={associatedContent?.selections || []}
            endCreditStart={endCreditStart} // A/B-test Skip Credits sep 2021
            nextVideoUrl={nextVideoUrl} // A/B-test Skip Credits sep 2021
            nextVideoId={nextVideoId} // A/B-test Skip Credits sep 2021
            videoLength={duration} // A/B-test Skip Credits sep 2021
            parentType={parent.__typename} // A/B-test Skip Credits sep 2021
        />
    );
};

const VideoModal = ({ id }) => {
    const [result] = useQuery({
        query: VIDEO_MODAL_QUERY,
        variables: {
            id: id,
        },
    });

    const { data, error, fetching } = result;

    // Video player break if no parent is present
    const hasData = Boolean(data?.detailsPage?.item?.parent);
    const isError = error || (!hasData && !fetching);
    const shouldRender = isError || hasData; // FocusTrap can only render if there is something focusable

    return ReactDOM.createPortal(
        <>
            <SlashAnimationWrapper>
                <SlashAnimation version="1.1" x="0px" y="0px" viewBox="0 0 16 16">
                    <polygon points="4.368,14.056 8.884,1.945 11.632,1.945 7.116,14.056 " />
                </SlashAnimation>
            </SlashAnimationWrapper>
            {shouldRender && (
                <main role="main">
                    <Container role="dialog" aria-modal="true" aria-label="video modal">
                        {hasData && <VideoModalPlay data={data} id={id} />}
                        {isError && (
                            <FocusTrap>
                                <VideoModalError data={data} error={error} id={id} />
                            </FocusTrap>
                        )}
                    </Container>
                </main>
            )}
        </>,
        document.getElementById('__next')
    );
};

// Component split in two allow early return before useQuery hook.
const VideoModalContainer = ({ id }) => {
    if (!id || !isBrowser()) {
        return null;
    }

    return (
        <>
            <VideoModal id={id} />
        </>
    );
};

export default VideoModalContainer;
